// src/Pages/ModuleRecords/PppArrangementAddRecord.jsx
// REG-PPP create/edit/view wizard — standalone, cross-cutting module.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { pppArrangementsApi } from '../../api/pppArrangementsApi';
import { CreateNewModal } from '../../component/Planning/PARCreateModal';
import RegistryCustomFields from '../../component/Admin/FoundationalTables/FormRegistry/RegistryCustomFields';
import { RegistryFormScope } from '../../component/Admin/FoundationalTables/FormRegistry/registryFieldOverrides';

export default function PppArrangementAddRecord({ viewOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams();
  const mode = uuid ? (viewOnly ? 'view' : 'edit') : 'create';

  const [row, setRow] = useState(null);
  const [loadingRow, setLoadingRow] = useState(Boolean(uuid));
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!uuid) { setRow(null); setLoadingRow(false); return; }
    const seedRow = location.state?.row;
    if (seedRow && (seedRow.uuid === uuid || seedRow.id === uuid)) {
      setRow(seedRow);
      setLoadingRow(false);
      return;
    }
    setLoadingRow(true);
    setLoadError('');
    pppArrangementsApi.get(uuid)
      .then((r) => setRow(r))
      .catch((e) => setLoadError(e?.response?.data?.message ?? e.message ?? 'Failed to load record.'))
      .finally(() => setLoadingRow(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const handleClose = useCallback(() => {
    navigate('/registry-management/ppp', { replace: true });
  }, [navigate]);

  const handleSubmit = useCallback(async (payload) => {
    await pppArrangementsApi.create(payload);
  }, []);

  const handleUpdate = useCallback(async (payload) => {
    await pppArrangementsApi.update(uuid, payload);
  }, [uuid]);

  return (
    <div className="min-h-screen bg-[#f7f7f7] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        {loadingRow && (
          <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
            Loading record…
          </div>
        )}
        {loadError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadError}
          </div>
        )}

        {(mode === 'create' || row) && (
          <RegistryFormScope moduleKey="ppp-arrangements" submoduleKey="arrangements">
            <>
              <RegistryCustomFields moduleKey="ppp-arrangements" submoduleKey="arrangements" />
              <CreateNewModal
                tab="ppp"
                onSubmit={mode === 'create' ? handleSubmit : handleUpdate}
                onClose={handleClose}
                inline
                mode={mode}
                row={mode === 'create' ? null : row}
                onEdit={() => navigate(`/ppp-arrangements/edit/${uuid}`, { state: { row } })}
                canEdit={true}
              />
            </>
          </RegistryFormScope>
        )}
      </div>
    </div>
  );
}
